import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["className", "maxFiles"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import uuid from 'uuid/v4';
import cn from 'classnames';
import { useDropzone } from 'react-dropzone';
import { find, unionBy, differenceBy } from 'lodash-es';
import { isFunction, paddingClass, safeInvoke, marginClass, isDefined } from '../../../library';
import { toaster } from '../../toaster';
import { Tooltip } from '../../tooltip';
import { Buttons, Button, Close, ObjectList, Pane } from '../../../components';
import { IconImage, IconAdd, IconTrash } from '../../../icons';
import { Column, Columns } from '../../columns';
var dropzoneClasses = {
  MAIN: 'dropzone',
  preview: 'dropzone__preview'
};
export function isTempFile(file) {
  return file.data !== undefined;
}
export var FileDropzone = function FileDropzone(props) {
  var _React$useState = React.useState([]),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    tempPreviews = _React$useState2[0],
    setTempPreviews = _React$useState2[1];
  React.useEffect(function () {
    setTempPreviews(props.files.filter(isTempFile).map(function (file) {
      var preview = URL.createObjectURL(file.data);
      return {
        id: file.id,
        url: preview
      };
    }));
    return function () {
      return tempPreviews.forEach(function (preview) {
        return URL.revokeObjectURL(preview.url);
      });
    };
  }, [props.files]);
  var isImage = function isImage(type) {
    return type.startsWith('image/');
  };
  var convertToTempFiles = function convertToTempFiles(files) {
    return files.map(function (file) {
      return {
        id: uuid(),
        data: file
      };
    });
  };
  var getImageAspectRatio = function getImageAspectRatio(e) {
    var _img$parentElement;
    var img = e.currentTarget;
    var ratio = img.naturalHeight / img.naturalWidth;
    (_img$parentElement = img.parentElement) === null || _img$parentElement === void 0 || _img$parentElement.classList.remove('fill-wide', 'fill-tall');
    if (ratio < 9 / 16) {
      var _img$parentElement2;
      (_img$parentElement2 = img.parentElement) === null || _img$parentElement2 === void 0 || _img$parentElement2.classList.add('fill-wide');
    } else {
      var _img$parentElement3;
      (_img$parentElement3 = img.parentElement) === null || _img$parentElement3 === void 0 || _img$parentElement3.classList.add('fill-tall');
    }
  };
  var handleDrop = function handleDrop(acceptedFiles, rejectedFiles) {
    var newFiles = convertToTempFiles(acceptedFiles);
    safeInvoke(props.onFileChange, unionBy(props.files, newFiles, 'id'));
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(function (file) {
        var message = 'This filetype is not allowed.';
        if (isDefined(props.maxSize) && file.size > props.maxSize) {
          message = 'The file size is too large.';
        }
        toaster.danger({
          text: message,
          heading: "Unable to use ".concat(file.name)
        });
      });
    }
  };
  var handleFileRemove = function handleFileRemove(files) {
    safeInvoke(props.onFileChange, differenceBy(props.files, files, 'id'));
  };
  var renderEmpty = function renderEmpty() {
    if (isFunction(props.renderEmpty)) return props.renderEmpty();
    return React.createElement("div", {
      className: cn('t--center', paddingClass([null, null, 6, null]))
    }, React.createElement("div", null, React.createElement(IconImage, {
      color: "#CCC"
    })), React.createElement("h5", null, "Drop Files Here"));
  };
  var renderActionBar = function renderActionBar(actions, api) {
    return React.createElement(Pane, null, React.createElement(Columns, null, React.createElement(Column, null, React.createElement(Buttons, {
      isInline: true,
      size: "small",
      appearance: "minimal"
    }, React.createElement(Button, {
      label: api.isAllSelected() ? 'Deselect All' : 'Select All',
      onClick: api.selectAllToggle
    }), actions.map(function (action) {
      return React.createElement(Button, {
        intent: action.intent || 'none',
        onClick: function onClick(e) {
          return action.fn();
        },
        before: action.icon,
        label: action.label
      });
    }))), React.createElement(Column, {
      span: 'shrink'
    }, React.createElement(Buttons, {
      size: "small",
      appearance: "minimal"
    }, React.createElement(Button, {
      onClick: open,
      intent: "primary",
      before: React.createElement(IconAdd, null)
    }, "Add Files")))));
  };
  var renderPreviews = function renderPreviews(files, api) {
    var dzApi = _objectSpread(_objectSpread({}, api), {}, {
      handleDelete: handleFileRemove
    });
    return React.createElement("div", {
      className: cn(dropzoneClasses.preview, {
        '-single': !props.multiple,
        '-multiple': props.multiple
      })
    }, files.map(function (file) {
      var preview = find(tempPreviews, ['id', file.id]);
      if (isFunction(props.renderPreview)) {
        return props.renderPreview(_objectSpread(_objectSpread({}, file), {}, {
          url: preview ? preview.url : undefined
        }), dzApi);
      } else {
        return renderDefaultPreview(_objectSpread(_objectSpread({}, file), {}, {
          url: preview ? preview.url : undefined
        }), dzApi);
      }
    }));
  };
  var renderDefaultPreview = function renderDefaultPreview(file, api) {
    if (isTempFile(file)) {
      if (props.multiple) {
        return React.createElement("div", {
          className: cn('file-preview', {
            '-selected': api.isSelected(file)
          }),
          key: file.id
        }, React.createElement("div", {
          className: "preview",
          onClick: function onClick(e) {
            return api.handleSelect(e, file, true);
          }
        }, React.createElement("img", {
          className: "thumbnail",
          src: file.url ? file.url : ''
        })), React.createElement(Tooltip, {
          position: "top",
          content: file.data.name,
          hoverOpenDelay: 500
        }, React.createElement("div", {
          className: "meta"
        }, file.data.name)), React.createElement(Close, {
          intent: "danger",
          appearance: "fill",
          onClick: function onClick() {
            return handleFileRemove([file]);
          }
        }));
      } else {
        return React.createElement("div", {
          className: "hero-preview",
          key: file.data.name + file.data.lastModified
        }, React.createElement("img", {
          src: file.url ? file.url : '',
          onLoad: getImageAspectRatio
        }));
      }
    } else {
      return 'a real asset';
    }
  };
  var className = props.className,
    maxFiles = props.maxFiles,
    dropzoneOptions = _objectWithoutProperties(props, _excluded);
  var _useDropzone = useDropzone(_objectSpread(_objectSpread({}, dropzoneOptions), {}, {
      onDrop: handleDrop
    })),
    getRootProps = _useDropzone.getRootProps,
    getInputProps = _useDropzone.getInputProps,
    inputRef = _useDropzone.inputRef,
    isFocused = _useDropzone.isFocused,
    isDragAccept = _useDropzone.isDragAccept,
    isDragActive = _useDropzone.isDragActive,
    isDragReject = _useDropzone.isDragReject,
    open = _useDropzone.open;
  var classNames = cn(dropzoneClasses.MAIN, {
    '-focused': isFocused,
    '-active': isDragActive,
    '-accept': isDragAccept,
    '-reject': isDragReject
  }, className);
  return React.createElement(React.Fragment, null, React.createElement("div", _extends({
    className: classNames
  }, getRootProps()), React.createElement(React.Fragment, null, React.createElement(ObjectList, {
    items: props.files,
    idProp: "id",
    isSelectable: true,
    actions: [].concat(_toConsumableArray(props.actions), [{
      id: 'delete',
      "for": 'all',
      label: 'Delete Selected',
      fn: handleFileRemove,
      icon: React.createElement(IconTrash, null),
      intent: 'danger'
    }])
  }, function (_ref) {
    var items = _ref.items,
      api = _ref.api,
      actions = _ref.actions;
    return React.createElement(React.Fragment, null, renderActionBar(actions, api), props.files.length === 0 ? renderEmpty() : renderPreviews(items, api));
  })), React.createElement("input", getInputProps())), React.createElement("div", {
    className: cn('t--sm t--center', marginClass([1]))
  }, props.helpText));
};
FileDropzone.displayName = 'FileDropzone';
FileDropzone.defaultProps = {
  actions: [],
  multiple: true,
  maxFiles: 0,
  noClick: true,
  noKeyboard: true
};